import { ApolloClient, ApolloLink, HttpLink, InMemoryCache, split } from '@apollo/client/core';
import { setContext } from '@apollo/client/link/context';
import { getMainDefinition } from '@apollo/client/utilities';
import { createClient } from 'graphql-ws';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import mitt from 'mitt';
import { v4 as uuidv4 } from 'uuid';
import { log } from '@/Utils/logger';
import { graphql } from '@/gql';

import { useSwalDialog } from '@/use/swalDialog';
import { tokenExpiredText } from '@/constants/app';
import { extractFrontendCommitHash } from '@/Utils/versionUtils';

const { showErrorPopupDialog } = useSwalDialog();

const httpLink = new HttpLink({
  uri: import.meta.env.VITE_API_URL + 'graphql',
});

const wsClient = createClient({
  url: `${import.meta.env.VITE_WEBSOCKET_URL}`,
  shouldRetry: () => true,
  keepAlive: 500000,
  on: {
    connecting: () => {
      log.info(
        {
          func: 'graphqlAPI.createClient',
        },
        'WebSocket connecting...',
      );
    },
    connected: () => {
      log.info(
        {
          func: 'graphqlAPI.createClient',
        },
        'WebSocket connected!',
      );
    },
    error: (error) => {
      log.error(
        {
          func: 'graphqlAPI.createClient',
          error,
        },
        'WebSocket error!',
      );
    },
    closed: () => {
      log.info(
        {
          func: 'graphqlAPI.createClient',
        },
        'WebSocket closed!',
      );
    },
  },
});

const wsLink = new GraphQLWsLink(wsClient);

const authLink = setContext((_, { headers }) => {
  const token = localStorage.getItem('access_token');
  return {
    headers: {
      ...headers,
      authorization: token ? `Bearer ${token}` : '',
    },
  };
});

export const versionEmitter = mitt<{ version: string }>();

const versionCheckLink = new ApolloLink((operation, forward) => {
  operation.setContext({
    ...operation.getContext(),
    uri: import.meta.env.VITE_API_URL + 'graphql?operation=' + operation.operationName,
  });

  return forward(operation).map((data) => {
    try {
      const versionHeader = operation.getContext().response?.headers?.get('x-version');
      if (typeof versionHeader === 'string') {
        versionEmitter.emit('version', extractFrontendCommitHash(versionHeader));
      }
    } catch (err) {
      log.error({ func: 'versionCheckLink', error: err }, 'Version check failed');
    }
    return data;
  });
});

const link = split(
  ({ query }) => {
    const definition = getMainDefinition(query);
    return definition.kind === 'OperationDefinition' && definition.operation === 'subscription';
  },
  wsLink,
  versionCheckLink.concat(authLink).concat(httpLink),
);

const apolloClient = new ApolloClient({
  link,
  cache: new InMemoryCache({
    addTypename: false,
  }),
  connectToDevTools: true,
});

const extractOperationDetails = (options: any) => {
  // either query or mutation is present, not both, not none
  const { query, mutation, ...restOptions } = options;
  const name = (query || mutation)?.definitions?.[0]?.name?.value || 'UNKNOWN';

  return {
    operationKind: query ? 'query' : 'mutation',
    name,
    ...restOptions,
  };
};

const runAndLogGql =
  <F extends (options: never) => Promise<unknown>>(operation: F) =>
  async (options: Parameters<F>[0]): Promise<Awaited<ReturnType<F>>> => {
    type Result = Awaited<ReturnType<F>>;
    const requestId = uuidv4();
    const { operationKind, name, ...restDetials } = extractOperationDetails(options);

    log.info(
      {
        func: 'runAndLogGql',
        options: {
          operationKind,
          name,
          ...restDetials,
        },
        requestId,
      },
      `GQL init of ${operationKind}:${name}`,
    );

    try {
      const result = (await operation(options)) as Result;

      log.info(
        {
          func: 'runAndLogGql',
          options: {
            operationKind,
            name,
            ...restDetials,
          },
          requestId,
        },
        `GQL success of ${operationKind}:${name}`,
      );

      return result;
    } catch (error: any) {
      const errorCode = error.networkError?.result?.code;
      const errorMessage = error.networkError?.result?.message ?? '';

      if (errorCode === 401 && errorMessage.includes(tokenExpiredText)) {
        showErrorPopupDialog({
          text: 'Your session has expired. Please log out and log back in to continue using the dashboard.',
        });
      }

      log.error(
        {
          func: 'runAndLogGql',
          options: {
            operationKind,
            name,
            ...restDetials,
          },
          error,
          requestId,
        },
        `GQL error of ${operationKind}:${name}`,
      );

      throw error;
    }
  };

const queryExecute = runAndLogGql(apolloClient.query.bind(apolloClient)) as typeof apolloClient.query;
const mutationExecute = runAndLogGql(apolloClient.mutate.bind(apolloClient)) as typeof apolloClient.mutate;

const subscribeExecute = apolloClient.subscribe.bind(apolloClient);

export default apolloClient;
export { queryExecute, mutationExecute, subscribeExecute };

// Heartbeat service for keeping the connection alive
export const heartbeatService = {
  /**
   * Sends a heartbeat ping to the server
   * @returns Promise that resolves when the heartbeat is successful
   */
  async ping(): Promise<void> {
    try {
      await apolloClient.mutate({
        mutation: graphql(`
          mutation Heartbeat {
            pong
          }
        `),
        errorPolicy: 'none',
        fetchPolicy: 'no-cache',
      });
    } catch (error) {
      log.error(
        {
          func: 'heartbeatService.ping',
          error,
        },
        'Heartbeat failed',
      );
      throw error;
    }
  },

  /**
   * Starts a heartbeat interval
   * @param intervalMs - Interval in milliseconds (default: 60000)
   * @returns The interval ID that can be used to clear the interval
   */
  startHeartbeat(intervalMs: number = 60000): ReturnType<typeof setInterval> {
    return setInterval(async () => {
      try {
        await this.ping();
      } catch (error) {
        log.error(
          {
            func: 'heartbeatService.startHeartbeat',
            error,
          },
          'Heartbeat interval failed',
        );
      }
    }, intervalMs);
  },
};
